import { useIsLoggedInQuery } from '../../common/query/useIsLoggedInQuery';
import { ZackeyAgentABT, ZackeyAgentFeatureToggle } from '../constants/abt';
import { useZackeyCheckQuery } from '../graphql/hooks/useZackeyCheckQuery';

export interface ZackeyPdpEntryState {
  shouldShow: boolean;
  sessionId?: string;
}

/**
 * 재키 PDP 진입점의 노출 상태와 세션 정보를 제공하는 훅
 *
 * 평가 순서:
 * 1. 로그인 체크 (로그인되지 않음/로딩/실패 → 미노출)
 * 2. FeatureToggle 체크 (on이 아님/로딩/실패 → 미노출)
 * 3. ABT 체크 (B 그룹이 아님/로딩/실패 → 미노출)
 * 4. zackey_check 쿼리 (enabled가 false/로딩/실패 → 미노출)
 *
 * 반드시 ZackeyAgentPDPEntryProvider 하위에서 호출해야 함
 *
 * @param catalogProductId - 상품 ID
 * @returns { shouldShow: boolean; sessionId?: string }
 */
export const useZackeyPdpEntry = (catalogProductId: string): ZackeyPdpEntryState => {
  // 1. 로그인 체크
  const { data: isLoggedIn, isLoading: isLoginLoading, isError: isLoginError } = useIsLoggedInQuery();

  // 2. FeatureToggle 체크
  const { isLoading: isFeatureToggleLoading, isFetched: isFeatureToggleFetched } =
    ZackeyAgentFeatureToggle.useABTStatusContext();
  const isFeatureToggleOn = ZackeyAgentFeatureToggle.useABTIsFeatureEnabled('on');

  // 3. ABT 체크
  const { isLoading: isAbtLoading, isFetched: isAbtFetched } = ZackeyAgentABT.useABTStatusContext();
  const isInBGroup = ZackeyAgentABT.useABTIsFeatureEnabled('B');

  // 조건 1~3 통과 여부 (zackey_check 호출 조건)
  const passedPreConditions =
    !isLoginLoading &&
    !isLoginError &&
    isLoggedIn &&
    isFeatureToggleFetched &&
    !isFeatureToggleLoading &&
    isFeatureToggleOn &&
    isAbtFetched &&
    !isAbtLoading &&
    isInBGroup &&
    catalogProductId.trim() !== '';

  // 4. zackey_check 쿼리 (조건부 호출)
  const { data: checkResult, isLoading: isCheckLoading } = useZackeyCheckQuery(
    { productId: catalogProductId },
    { enabled: passedPreConditions },
  );

  // 최종 노출 여부
  const shouldShow = passedPreConditions && !isCheckLoading && checkResult?.enabled === true;

  return {
    shouldShow: shouldShow ?? false,
    sessionId: checkResult?.session?.session_id,
  };
};
